import React, { useState, useEffect } from 'react';
import {
  Avatar, Typography, Box, FormControl, InputLabel, Select, MenuItem, 
  TextField, Grid, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Divider, Chip
} from '@mui/material';
import { UploadFile, Save, Send, PersonAdd, Person, Email, Security } from '@mui/icons-material';
import axiosInstance from './axiosConfig';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import AlertSnackbar from './AlertSnackbar';
import { useLanguage } from '../contexts/LanguageContext';

const apiUrl = process.env.REACT_APP_API_URL;

const UserProfile = () => {
  const { translate } = useLanguage();
  const [user, setUser] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAvatar, setNewAvatar] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosInstance.get(`/api/user`);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user data', error);
      }
    };

    const fetchProjects = async () => {
      try {
        const response = await axiosInstance.get(`/api/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects', error);
      }
    };

    const fetchInvitations = async () => {
      try {
        const response = await axiosInstance.get(`/api/project/invitations`);
        setInvitations(response.data);
      } catch (error) {
        console.error('Error fetching invitations', error);
      }
    };

    fetchUserData();
    fetchProjects();
    fetchInvitations();
  }, []);

  const handleInvite = async () => {
    if (!selectedProjectId) {
      showSnackbar(translate('userProfile.selectProjectFirst'), 'error');
      return;
    }

    try {
      await axiosInstance.post(`/api/project/invite`, {
        email: inviteEmail,
        projectId: selectedProjectId,
      });
      setInviteEmail('');
      showSnackbar(translate('userProfile.invitationSent'), 'success');
    } catch (error) {
      showSnackbar(translate('userProfile.invitationError'), 'error');
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append('email', newEmail);
      formData.append('password', newPassword);
      formData.append('avatar', newAvatar);

      await axiosInstance.post(`/api/user/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUser({ ...user, email: newEmail, avatar: newAvatar });
      showSnackbar(translate('userProfile.profileUpdated'), 'success');
    } catch (error) {
      showSnackbar(translate('userProfile.updateError'), 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <ModernLayout title={translate('userProfile.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('userProfile.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('userProfile.subtitle')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Proyectos"
            value={projects.length.toString()}
            subtitle="Proyectos disponibles"
            icon={<Person />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Invitaciones"
            value={invitations.length.toString()}
            subtitle="Invitaciones enviadas"
            icon={<Email />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Rol"
            value={user.role || 'Usuario'}
            subtitle="Privilegios actuales"
            icon={<Security />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Estado"
            value="Activo"
            subtitle="Cuenta verificada"
            icon={<Person />}
            color="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Información del Perfil */}
        <Grid item xs={12} md={6}>
          <ModernCard
            title={translate('userProfile.profileInformation')}
            subtitle={translate('userProfile.managePersonalInfo')}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mb: 3 }}>
                <Avatar
                  alt="Avatar"
                  src={user.avatar ? `${apiUrl}/uploads/${user.avatar}` : '/default-avatar.png'}
                  sx={{ 
                    width: 100, 
                    height: 100,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    mb: 2,
                  }}
                />
                <input
                  accept="image/*"
                  type="file"
                  onChange={(e) => setNewAvatar(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="avatar-upload"
                />
                <label htmlFor="avatar-upload">
                  <GlassButton
                    component="span"
                    startIcon={<UploadFile />}
                  >
                    {translate('userProfile.changeAvatar')}
                  </GlassButton>
                </label>
              </Box>
              
              <TextField
                label={translate('userProfile.newEmail')}
                fullWidth
                margin="normal"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={translate('userProfile.enterNewEmail')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <TextField
                label={translate('userProfile.newPassword')}
                type="password"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={translate('userProfile.enterNewPassword')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <GradientButton
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={!newEmail && !newPassword && !newAvatar}
                >
                  {translate('userProfile.saveChanges')}
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>

        {/* Invitar a un Usuario */}
        <Grid item xs={12} md={6}>
          <ModernCard
            title={translate('userProfile.sendInvitation')}
            subtitle={translate('userProfile.inviteNewUser')}
          >
            <Box sx={{ p: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>{translate('userProfile.selectProject')}</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={translate('userProfile.invitationEmail')}
                fullWidth
                margin="normal"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={translate('userProfile.enterInvitationEmail')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <GradientButton
                  startIcon={<Send />}
                  onClick={handleInvite}
                  disabled={!selectedProjectId || !inviteEmail}
                >
                  {translate('userProfile.sendInvitation')}
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>

        {/* Lista de Invitaciones */}
        <Grid item xs={12}>
          <ModernCard
            title={translate('userProfile.invitationsSent')}
            subtitle={translate('userProfile.sentInvitationsHistory')}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{translate('userProfile.invitedEmail')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{translate('userProfile.project')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{translate('userProfile.status')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{translate('userProfile.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            }}
                          >
                            {invitation.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">
                            {invitation.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.project_name}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.status}
                          size="small"
                          color={invitation.status === 'accepted' ? 'success' : 
                                invitation.status === 'pending' ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(invitation.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ModernCard>
        </Grid>
      </Grid>

      <AlertSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </ModernLayout>
  );
};

export default UserProfile;
